import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator'
import { AxiosResponse } from 'axios'
import { BFormGroup, BRow, BCol, BFormRadioGroup, BFormRadio, BButton, VBTooltip } from 'bootstrap-vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { FormModal, TeamUserSelect } from '@kwixeo/interface-vue'
import { KForm, KFormInput, KFormSelect, KFormSpinner, KFormCheckbox, KFormCheckboxGroup } from '@kwixeo/form-vue'
import icons from '../../icons'
import { mapState } from 'vuex'
import { TranslateResult } from 'vue-i18n'

@Component({
  components: {
    FormModal, TeamUserSelect,
    KForm, KFormInput, KFormSelect, KFormSpinner, KFormCheckbox, KFormCheckboxGroup,
    BFormGroup, BRow, BCol, BFormRadioGroup, BFormRadio, BButton,
    FontAwesomeIcon
  },
  computed: {
    ...mapState('dashboard', ['widgets']),
    ...mapState('globalDatas', ['teamUsers'])
  },
  directives: {
    'b-tooltip': VBTooltip
  }
})
export default class WidgetForm extends Vue {
  widgets!: KwixeoDashboard.AvailableWidgets
  teamUsers!: KwixeoFramework.TeamUsers

  @Prop({ default: null }) dashboard!: KwixeoDashboard.Dashboard
  @Prop({ default: null }) value!: KwixeoDashboard.Widget
  @Prop({ default: false }) modal!: boolean

  private widget: KwixeoDashboard.WidgetForm = { params: {}, type: 'number' }
  private modalState = false
  private availableIcons = icons

  private chronologicalType = [
    { label: 'Barre', value: 'bar' },
    { label: 'Courbe', value: 'curve' },
    { label: 'Courbe cumulative', value: 'cumulative' }
  ]

  private periodOptions = [
    { label: 'Période sélectionné sur le tableau de bord', value: 'default' },
    { label: 'Année en cours', value: 'year' },
    { label: 'Mois en cours', value: 'month' },
    { label: 'Semaine en cours', value: 'week' },
    { label: 'Journée en cours', value: 'day' },
    { label: 'Personnalisée', value: 'custom' }
  ]

  private customPeriodType = [
    { label: 'Derniers mois', value: 'month' },
    { label: 'Dernières semaines', value: 'week' },
    { label: 'Derniers jours', value: 'day' }
  ]

  get formAction (): string {
    let url = '/dashboard/'
    if (this.dashboard?.id) {
      url += this.dashboard.id
    } else if (this.value?.id) {
      url += 'widget/' + this.value.id
    }
    return url
  }

  get formDatas (): KwixeoInterface.GenericObject {
    const val = this.widget as KwixeoDashboard.Widget

    const params: KwixeoInterface.GenericObject = {}

    if (val.type !== 'list') {
      params.dont_compare = val.params.dont_compare
      params.period = val.params.period
      if (val.params.period !== 'default') {
        params.period_type = val.params.period_type
        params.period_value = val.params.period_value
      }
    }

    switch (val.type) {
      case 'number':
        params.key = val.params.key
        params.main_value = val.params.main_value
        params.secondary_value = val.params.secondary_value
        params.third_value = val.params.third_value
        params.icon = val.params.icon
        params.icon_color = val.params.icon_color
        break
      case 'bar':
        params.key = val.params.key
        params.limit = val.params.limit
        params.value = val.params.value
        break
      case 'diagram':
        params.key = val.params.key
        params.limit = val.params.limit
        params.value = val.params.value
        break
      case 'chronological':
      case 'chronological-table':
        params.datas = val.params.datas
        break
      case 'list':
        params.key = val.params.key
        params.limit = val.params.limit
        break
    }

    if (val.params.filters) {
      params.filters = JSON.parse(JSON.stringify(val.params.filters))
    }

    const widget = {
      type: val.type,
      title: val.title,
      background_color: val.background_color,
      text_color: val.text_color,
      width: val.width,
      height: val.height,
      params
    }

    if (this.value?.id) {
      return widget
    }
    return { add_widgets: [widget] }
  }

  get inputName (): (key: string) => string {
    return key => {
      return 'add_widgets[0][' + key + ']'
    }
  }

  get modalTitle (): TranslateResult {
    return this.$t('dashboard.widget.' + (this.value?.id ? 'editTitle' : 'addTitle'))
  }

  get datasTypes (): KwixeoDashboard.AvailableWidgets {
    return this.widgets.filter(widget => widget.types.includes(this.widget.type))
      .sort(function (a, b) {
        return a.title.localeCompare(b.title)
      })
  }

  get datasSource (): KwixeoDashboard.AvailableWidgetDatas {
    const data = this.widgets.find(widget => this.widget.params && 'key' in this.widget.params && widget.id === this.widget.params?.key)
    if (!data) {
      return []
    }

    return data.datas
  }

  get datasFilters (): KwixeoDashboard.AvailableFilters {
    if (this.widget.type === 'chronological') {
      const filters: KwixeoDashboard.AvailableFilters = []
      this.widget.params.datas.forEach((dataSource: KwixeoDashboard.ChronologicalDatasSource) => {
        const data = this.widgets.find(widget => widget.id === dataSource.key)
        if (data) {
          filters.push(...data.filters)
        }
      })

      return filters.filter((filter, index) => {
        const compareIndex = filters.findIndex(val => val.key === filter.key)
        return compareIndex === index
      })
    }

    const data = this.widgets.find(widget => this.widget.params && 'key' in this.widget.params && widget.id === this.widget.params?.key)
    if (!data) {
      return []
    }
    return data.filters
  }

  get datasFromSource (): (key: string) => KwixeoDashboard.AvailableWidgetDatas {
    return key => {
      const data = this.widgets.find(widget => widget.id === key)
      if (!data) {
        return []
      }
      return data.datas
    }
  }

  private onDatasSourceSelected (index: number): void {
    this.widget.params.datas[index].value = this.datasFromSource(this.widget.params.datas[index].key).length === 1 ? this.datasFromSource(this.widget.params.datas[index].key)[0].key : null

    const data = this.widgets.find(widget => widget.id === this.widget.params.datas[index].key)
    if (data) {
      data.filters.forEach(filter => {
        if (!this.widget.params.filters[filter.key]) {
          this.widget.params.filters[filter.key] = filter.default
        }
      })

      if (index === 0) {
        this.widget.title = data ? data.title : null
      }

      const value = this.widget.params.datas[index]
      const source = this.widgets.find(widget => widget.id === value.key)
      if (source) {
        value.name = source.title
      }
    }
  }

  @Watch('modal', { immediate: true })
  private watchModal (state: boolean): void {
    this.modalState = state
    if (state) {
      this.watchValue(this.value)
    }
  }

  @Watch('value', { immediate: true })
  private watchValue (widget: KwixeoDashboard.Widget|null): void {
    this.widget = Object.assign(
      {
        title: null,
        background_color: '#FFFFFF',
        text_color: '#666666',
        type: 'chronological',
        width: 12,
        height: 3,
        params: {},
      },
        widget ? JSON.parse(JSON.stringify(widget)) : {}
      )

    this.widget.params = Object.assign({
      key: null,
      period: 'default',
      period_type: 'month',
      period_value: 12,
      dont_compare: false,
      value: null,
      main_value: null,
      secondary_value: null,
      third_value: null,
      icon: 'file-invoice',
      icon_color: '#666666',
      limit: 10,
      datas: [],
      filters: {}
    }, this.widget.params)
  }

  @Emit('submitComplete')
  private formSubmitted (response: AxiosResponse): AxiosResponse {

    return response.data
  }

  private resetWidgetType (): void {
    const widget = this.widget as KwixeoDashboard.Widget
    switch (widget.type) {
      case 'number':
      case 'bar':
      case 'diagram':
        this.widget.params.key = null
        break
    }

    switch (widget.type) {
      case 'number':
        widget.width = 3
        widget.height = 1
        break
      case 'bar':
        widget.width = 4
        widget.height = 3
        break
      case 'diagram':
        widget.width = 4
        widget.height = 3
        break
      case 'chronological':
        widget.width = 12
        widget.height = 3
        break
    }

  }

  private resetSelectedDatas (): void {
    if (!this.widget.params) {
      return
    }

    if (this.widget.type === 'number') {
      this.widget.params.main_value = null
      this.widget.params.secondary_value = null
      this.widget.params.third_value = null
    } else if (this.widget.type !== 'chronological') {
      this.widget.params.value = this.datasSource.length === 1 ? this.datasSource[0].key : null
    }

    if (this.widget.type !== 'chronological' && this.datasFilters.length > 0) {
      const filters: KwixeoDashboard.WidgetFilters = {}
      this.datasFilters.forEach(filter => {
        filters[filter.key] = filter.default ?? []
      })
      this.widget.params.filters = filters
    }

    if (this.widget.type !== 'chronological') {
      const source = this.widgets.find(widget => this.widget.params && 'key' in this.widget.params && widget.id === this.widget.params?.key)
      this.widget.title = source ? source.title : null
    }
  }

  private addDataSource (): void {
    this.widget.params.datas.push({
      key: null,
      value: null,
      type: 'curve',
      name: null,
      color: '#' + Math.floor(Math.random() * 16777215).toString(16)
    })
  }

  private removeDataSource (index: null): void {
    this.widget.params.datas.splice(index, 1)
  }
}
